import { ToolTipPlacement } from '../../../../types/componentTypes/tooltip';
import { PageErrors } from '../types/AdditionalDetailsTypes';

export const MIN_AGE = 12;
export const MAX_AGE = 150; // sensible max age default
export const PARTICIPATION_IDS_MAX = 100;
export const PARTICIPATION_IDS_MIN = 1;
export const PARTICIPATION_IDS_DEFAULT = 10;

const promoFlowCheckerLambdas = {
    minAge: ['age'],
};

export const flowMap = {
    queryVouchers: {
        flowLambdas: ['digitalCodesQueryByUserLambda'],
    },
    listPrizes: {
        flowLambdas: ['prizeQueryLambda'],
        params: { additionalInformation: true },
    },
    redeemPrize: {
        checkerLambdas: ['prizeCheckerLambda', 'currencyCheckerLambda'],
        flowLambdas: ['prizeRedeemLambda'],
    },
    addTransaction: {
        flowLambdas: ['transactionLambda'],
    },
    listTransactions: {
        flowLambdas: ["listTransactionsByUserLambda"]
    },
    redeemVoucher: {
        flowLambdas: ['digitalCodesRedeemLambda'],
    },
    queryWallet: {
        flowLambdas: ['walletLambda'],
    },
    redeemPincodeForCurrencies: {
        flowLambdas: ['burnPincodes', 'pincodeToCurrency', 'transactionLambda'],
    },
    acceptReservedVoucher: {
        flowLambdas: ['acceptReservedVoucherLambda'],
        params: { maxParticipationIds: PARTICIPATION_IDS_DEFAULT },
    },
    rejectReservedVoucher: {
        flowLambdas: ['rejectReservedVoucherLambda'],
    },
    rejectWinningMoment: {
        flowLambdas: ['rejectWinningMomentLambda'],
    },
    additionalInformation: {
        flowLambdas: ['additionalConfigurationInformationLambda']
    }
} as const;

export const ToolTipProps = {
    baseProps: {
        styleName: 'details-checkbox-tooltip',
        textVariant: 'body1' as const,
        placement: ToolTipPlacement.RightEnd,
    },
    queryWalletProps: {
        text: 'Wallet',
        dataTestId:'wallet-tooltip',
        tooltip: 'Once enabled, participants will be able to see their wallets',
    },
    azpProps:{
        text: 'Validate experience ID',
        tooltip: 'Allows you to validate the experience ID for the promotion',
    },
    'Collect & Get'() {
        return {
            redeemPrize: {
                ...this.baseProps,
                text: 'Prize Redemption',
                dataTestId:'prize-redemption-tooltip',
                tooltip: 'Once enabled, participants can redeem prizes even if no vouchers are distributed',
            },
            addTransaction: {
                ...this.baseProps,
                text: 'Transactions',
                dataTestId:'transactions-tooltip',
                tooltip: "Once enabled, the amount of every participant's wallet can be modified",
            },
            listTransactions: {
                ...this.baseProps,
                text: 'List Transactions',
                tooltip: "Once enabled, the consumer's transactions can be listed.",
            },
            queryVouchers: {
                ...this.baseProps,
                text: 'Vouchers History',
                dataTestId:'vouchers-history-tooltip',
                tooltip: 'Once enabled, vouchers history can be shown to participants',
            },
            redeemVoucher: {
                ...this.baseProps,
                text: 'Vouchers Redemption',
                dataTestId:'vouchers-redemption-tooltip',
                tooltip: 'This functionality gives participants the ability to redeem vouchers for prizes',
            },
            queryWallet: {
                ...this.baseProps,
                ...this.queryWalletProps,
            },
            listPrizes: {
                ...this.baseProps,
                text: 'Enable Prize Catalog',
                dataTestId:'prize-catalog-tooltip',
                tooltip: 'Allows you to show the prize catalog',
            },
            captchaSecret: {
                ...this.baseProps,
                text: 'Web Integration',
                dataTestId:'web-integration-tooltip',
                tooltip: 'Allows you to add a captcha secret to the promotion',
            },
            azp: {
                ...this.baseProps,
                ...this.azpProps,
                dataTestId:'azp-check-tooltip',
            },
            enterpriseCaptcha: {
                ...this.baseProps,
                text: 'Web Integration',
                dataTestId:'web-integration-tooltip',
                tooltip: 'Use enterprise captcha for the promotion. If you are not using the default project id, please contact Promo+ team',
            },
            promoTeaser: {
                ...this.baseProps,
                text: 'Promotion Teaser',
                dataTestId:'promotion-teaser-tooltip',
                tooltip: `Allows you to enable a promotion teaser before the start date to build consumers' awareness. This option will be automatically disabled if the current date is older than the promotion start date`,
            },
            minAge: {
                ...this.baseProps,
                text: 'Minimum Age Limit',
                dataTestId:'min-age-tooltip',
                tooltip: `If option is enabled, age range is between ${MIN_AGE} and ${MAX_AGE}`,
            },
        };
    },

    'Prize Draw (Lottery)'() {
        return {
            imageEntry: {
                ...this.baseProps,
                text: 'Image Promotion Entry',
                dataTestId:'image-promotion-entry-prize-draw',
                tooltip: 'The Image Promo Entry option will enable an image upload as a promotion entry',
            },
            queryWallet: {
                ...this.baseProps,
                ...this.queryWalletProps,
            },
            captchaSecret: {
                ...this.baseProps,
                text: 'Add Captcha Secret',
                dataTestId:'add-captcha-secret-prize-draw',
                tooltip: 'Allows you to add a captcha secret to the promotion',
            },
            azp: {
                ...this.baseProps,
                ...this.azpProps,
                dataTestId:'add-azp-check-prize-draw',
            },
            enterpriseCaptcha: {
                ...this.baseProps,
                text: 'Add Enterprise Captcha Details',
                dataTestId:'add-captcha-secret-prize-draw',
                tooltip: 'Use enterprise captcha for the promotion. If you are not using the default project id, please contact Promo+ team',
            },
            customAdditionalInfo: {
                ...this.baseProps,
                text: 'Additional Information',
                tooltip: 'In additionalInfo option, you can add custom key-value pairs which will be added as a parameter in the additionalInfo.',
            },
            legacyAdditionalInformation: {
                ...this.baseProps,
                text: 'List Additional Information',
                dataTestId:'list-additional-info',
                tooltip: 'This is a legacy option where you can list all the custom additional information.',
            },
            promoTeaser: {
                ...this.baseProps,
                text: 'Promotion Teaser',
                dataTestId:'promotion-teaser-tooltip-prizeDraw',
                tooltip: `Allows you to enable a promotion teaser before the start date to build consumers' awareness. This option will be automatically disabled if the current date is older than the promotion start date`,
            },
        };
    },

    'Lucky Numbers'() {
        return {
            imageEntry: {
                ...this.baseProps,
                text: 'Image Promotion Entry',
                dataTestId:'image-promotion-entry-lucky-numbers',
                tooltip: 'The Image Promo Entry option will enable an image upload as a promotion entry',
            },
            queryVouchers: {
                ...this.baseProps,
                text: 'Lucky Numbers History',
                dataTestId:'vouchers-history-lucky-numbers',
                tooltip: 'Once enabled, lucky numbers history can be shown to participants',
            },
            captchaSecret: {
                ...this.baseProps,
                text: 'Add Captcha Secret',
                dataTestId:'add-captcha-secret-lucky-numbers',
                tooltip: 'Allows you to add a captcha secret to the promotion',
            },
            azp: {
                ...this.baseProps,
                ...this.azpProps,
                dataTestId:'add-azp-check-lucky-numbers',
            },
            enterpriseCaptcha: {
                ...this.baseProps,
                text: 'Add Enterprise Captcha Details',
                dataTestId:'add-captcha-secret-lucky-numbers',
                tooltip: 'Use enterprise captcha for the promotion. If you are not using the default project id, please contact Promo+ team',
            },
            customAdditionalInfo: {
                ...this.baseProps,
                text: 'Additional Information',
                tooltip: 'In additionalInfo option, you can add custom key-value pairs which will be added as a parameter in the additionalInfo.',
            },
            promoTeaser: {
                ...this.baseProps,
                text: 'Promotion Teaser',
                dataTestId:'promotion-teaser-tooltip-lucky-numbers',
                tooltip: `Allows you to enable a promotion teaser before the start date to build consumers' awareness. This option will be automatically disabled if the current date is older than the promotion start date`,
            },
        };
    },

    'Instant Win'() {
        return {
            queryVouchers: {
                ...this.baseProps,
                text: 'Voucher History',
                dataTestId:'voucher-history-instant-win',
                tooltip: 'Allows you to show the history of claimed vouchers per participant',
            },
            queryWallet: {
                ...this.baseProps,
                ...this.queryWalletProps,
            },
            voucherAvailabilityTooltip: {
                ...this.baseProps,
                text: 'Vouchers Availability',
                dataTestId:'vouchers-availability-tooltip',
                tooltip: 'Allows you to prevent consumers from burning pincodes when there are not enough vouchers available in the promotion.',
            },
            listPrizes: {
                ...this.baseProps,
                text: 'Enable Prize Catalog',
                dataTestId:'enable-prize-catalog',
                tooltip: 'Allows you to show the prize catalog',
            },
            promoTeaser: {
                ...this.baseProps,
                text: 'Promotion Teaser',
                dataTestId:'promotion-teaser-instant-win',
                tooltip: `Allows you to enable a promotion teaser before the start date to build consumers' awareness. This option will be automatically disabled if the current date is older than the promotion start date`,
            },
            captchaSecret: {
                ...this.baseProps,
                text: 'Add Captcha Secret',
                dataTestId:'add-captcha-secret-instant-win',
                tooltip: 'Allows you to add a captcha secret to the promotion',
            },
            azp: {
                ...this.baseProps,
                ...this.azpProps,
                dataTestId:'add-azp-check-instant-win',
            },
            enterpriseCaptcha: {
                ...this.baseProps,
                text: 'Add Enterprise Captcha Details',
                dataTestId:'add-captcha-secret-instant-win',
                tooltip: 'Use enterprise captcha for the promotion. If you are not using the default project id, please contact Promo+ team',
            },
            interruptedFlow: {
                ...this.baseProps,
                text: 'Enable Interrupted Flow',
                dataTestId:'enable-interrupted-flow-instant-win',
                tooltip:
                    `Once enabled, Reserved status will be used for vouchers that are distributed within this promotion.

                    The values defines how many vouchers can be accepted in a single request.
                    `,
            },
            imageEntry: {
                ...this.baseProps,
                text: 'Image Promotion Entry',
                dataTestId:'image-promotion-entry-iw',
                tooltip: 'The Image Promo Entry option will enable an image upload as a promotion entry',
            },
            customAdditionalInfo: {
                ...this.baseProps,
                text: 'Additional Information',
                dataTestId:'additional-information-instant-win',
                tooltip: 'In additionalInfo option, you can add custom key-value pairs which will be added as a parameter in the additionalInfo.',
            },
        };
    },
} as const;

export const componentConfigPropKeys = {
    minAge(promoFlow: string) {
        return {
            checkBox: JSON.stringify([
                'formState.minAgeEnabled',
                `flow.${promoFlow}.params.minAge`,
                'configurationParameters.additionalInformation.minAge',
                { key: `flow.${promoFlow}.checkerLambdas`, value: promoFlowCheckerLambdas.minAge },
            ]),
            textField: [
                `flow.${promoFlow}.params.minAge`,
                'configurationParameters.additionalInformation.minAge',
            ].toString(),
        };
    },
    promoTeaser() {
        return {
            checkBox: [
                'formState.promotionTeaserEnabled',
                'configurationParameters.additionalInformation.visibleFromDate',
            ].toString(),
            datePicker: ['configurationParameters.additionalInformation.visibleFromDate'].toString(),
        };
    },
    interruptedFlow() {
        return {
            checkBox: [
                'flow.instantWin.params.useStatusReserved',
                'flow.acceptReservedVoucher',
                'flow.rejectReservedVoucher',
                'flow.rejectWinningMoment',
            ].toString(),
            textField: ['flow.acceptReservedVoucher.params.maxParticipationIds'].toString(),
        };
    },
    captchaSecret() {
        return {
            checkBox: ['formState.captchaSecretEnabled', 'configurationParameters.captchaSecret'].toString(),
            textField: ['configurationParameters.captchaSecret'].toString(),
        };
    },
    azp() {
        return {
            checkBox: ['formState.azpCheckEnabled', 'configurationParameters.azp'].toString(),
            textField: ['configurationParameters.azp'].toString(),
        };
    },
    enterpriseCaptcha() {
        return {
            checkBox: ['formState.enterpriseCaptchaEnabled', 'configurationParameters.captchaDetails'].toString(),
            textFieldProject: ['configurationParameters.captchaDetails.projectId'].toString(),
            textFieldSiteKey: ['configurationParameters.captchaDetails.siteKey'].toString()
        };
    },
    listPrizes() {
        return {
            checkBox: ['flow.listPrizes'].toString(),
            radioGroup: ['flow.listPrizes.params.priorityOrder'].toString(),
        };
    },
    wvUrl: () => ['configurationParameters.additionalInformation.wv_url'].toString(),
    redeemPrize: () => ['flow.redeemPrize'].toString(),
    addTransaction: () => ['flow.addTransaction'].toString(),
    listTransactions: () => ['flow.listTransactions'].toString(),
    queryVouchers: () => ['flow.queryVouchers'].toString(),
    voucherAvailability: () => ['flow.instantWin.params.voucherAvailability'].toString(),
    redeemVoucher: () => ['flow.redeemVoucher'].toString(),
    queryWallet: () => ['flow.queryWallet'].toString(),
    listAdditionalInformation: () => ['formState.listAdditionalInformation', 'flow.additionalInformation'].toString(),
    imageEntry: () => ['flow.promoEntry.params.imageEntry'].toString(),
    imageEntryIW: () => ['flow.instantWin.params.imageEntry'].toString(),
    tags: () => ['configurationParameters.additionalInformation.tags'].toString(),
    identityProvider: () => ['configurationParameters.userIdType'].toString(),
    customAdditionalInfo() {
        return {
            checkBox: ['formState.additionalInfoEnabled', 'configurationParameters.additionalInformation'].toString(),
            textField: ['configurationParameters.additionalInformation'].toString(),
        };
    },
} as const;

export const pageErrorsMap: PageErrors = new Map();

export const identityProviders = [
    { value: 'cds', label: 'CDS' },
    { value: 'cid', label: 'CID' },
] as const;
